import { Avatar, Box, SxProps } from '@mui/material';
import type { KeyboardEventHandler, MouseEventHandler } from 'react';
import {
  CircleBlueCloseButton,
  CircleBlueCloseButtonLarge,
  CircleBlueCloseButtonMedium,
  CirclePurpleCloseButton,
  PurpleClose,
} from 'Images';

export type CircleCloseIconSize = 'small' | 'medium' | 'large';

const SIZE_PX: Record<CircleCloseIconSize, number> = {
  small: 24,
  medium: 32,
  large: 40,
};

const ICONS: Record<CircleCloseIconSize, { blue: string; purple: string }> = {
  small: { blue: CircleBlueCloseButton, purple: PurpleClose },
  medium: { blue: CircleBlueCloseButtonMedium, purple: PurpleClose },
  large: {
    blue: CircleBlueCloseButtonLarge,
    purple: CirclePurpleCloseButton,
  },
};

interface ICircleCloseIcon {
  size?: CircleCloseIconSize;
  alt?: string;
  sx?: SxProps;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
}

export const CircleCloseIcon = ({
  size = 'medium',
  alt = '',
  sx,
  onClick,
  onKeyDown,
}: ICircleCloseIcon) => {
  const px = SIZE_PX[size];
  const icons = ICONS[size];
  const avatarSx = {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    transition: 'opacity 0.15s ease-in-out',
  };

  const isInteractive = Boolean(onClick || onKeyDown);

  return (
    <Box
      component={isInteractive ? 'button' : 'span'}
      type={isInteractive ? 'button' : undefined}
      onClick={onClick}
      onKeyDown={onKeyDown}
      aria-label={isInteractive && alt ? alt : undefined}
      aria-hidden={!isInteractive && alt === '' ? true : undefined}
      sx={{
        border: 'none',
        background: 'none',
        padding: 0,
        cursor: isInteractive ? 'pointer' : undefined,
        display: 'inline-flex',
        flexShrink: 0,
        width: `${px}px`,
        height: `${px}px`,
        position: 'relative',
        '& .circle-close-icon-blue': { opacity: 1 },
        '& .circle-close-icon-purple': { opacity: 0 },
        '&:hover .circle-close-icon-blue': { opacity: 0 },
        '&:hover .circle-close-icon-purple': { opacity: 1 },
        ...sx,
      }}
    >
      <Avatar
        className='circle-close-icon-blue'
        src={icons.blue}
        alt={alt}
        sx={avatarSx}
      />
      <Avatar
        className='circle-close-icon-purple'
        src={icons.purple}
        alt=''
        sx={avatarSx}
      />
    </Box>
  );
};
